/**
 * Row-hover tooltip content for medical PA orders worklist tables.
 *
 * @module medicalPaOrderRowTooltip
 */
import { ReactNode } from "react";
import { Hit } from "../data-model/postgresResponseForMedicalPaOrders";
import { MedicalOrderStatus } from "../enums/orderReviewStatus";
import { AgentAiIcon } from "../svg/agent-ai-icon";
import InProgress from "../svg/inProgress";
import { isInvalidMedicalOrderStatus } from "./isInvalidMedicalOrderStatus";

const tooltipClassName =
  "bg-white text-tiny font-medium text-primaryGray-3 inline-flex items-center gap-1";

/**
 * Returns row-hover tooltip JSX for a medical PA orders table row.
 * Shown only when the order is in development (empty status) or agent-in-progress.
 *
 * @param row - Full table row object from medical PA orders table generators
 * @returns Tooltip content, or null when the order status does not apply
 */
export const getMedicalPaOrderRowTooltip = (
  row: Record<string, unknown> | undefined,
): ReactNode | null => {
  if (!row) {
    return null;
  }

  const parsedJsonRowData = JSON.parse(row.rowData as string) as Hit;
  const medicalPaOrderStatus = parsedJsonRowData?.medical_order_status;

  // Empty or missing status means the order is still being set up in the platform.
  if (isInvalidMedicalOrderStatus(medicalPaOrderStatus)) {
    return (
      <span className={tooltipClassName}>
        <InProgress />
        Order In Development
      </span>
    );
  }

  // Agent-owned orders stay on the worklist until processing completes.
  if (medicalPaOrderStatus === MedicalOrderStatus.AGENT_IN_PROGRESS) {
    return (
      <span className={tooltipClassName}>
        <AgentAiIcon fill="#E65100" height={22} width={22} />
        Agent In Progress
      </span>
    );
  }

  return null;
};
